/**
 * ============================================================================
 * ProgramPageConnectModalForm — Service (API calls)
 * ============================================================================
 * Pure fetch wrappers. No JSX, no React state.
 * lead-create-new only (NOT lp-new). Lead-check + prospect-activity +
 * connect-counsellor match pre-MVC ProgramPageConnectModalForm.
 * ============================================================================
 */
import { getIpJSONData } from "@/context/ip.storage.utils";
import { formatDate, generateUniqueId } from "../../ConnectModalForm/utils";
import { leadCreateFetch } from "@/utils/connectCounsellor";

export interface LeadCheckPayload {
  phoneNumber: string;
  emailId: string;
  fullName: string;
  countryCode: string;
}

/** Ensures/returns the anonymous uniqueLeadId used for lead-check dedupe. */
export function getOrCreateUniqueLeadId(): string {
  let uniqueId = localStorage.getItem("uniqueLeadId");
  if (!uniqueId) {
    uniqueId = generateUniqueId();
    localStorage.setItem("uniqueLeadId", uniqueId);
  }
  return uniqueId;
}

/**
 * POST /api/leadsquared.com/lead-check — silent, best-effort dedupe ping.
 * Matches pre-MVC ProgramPage payload (PhoneNo = phone only).
 */
export async function checkExistingLead(
  payload: LeadCheckPayload,
): Promise<void> {
  try {
    const response = await fetch("/api/leadsquared.com/lead-check", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        PhoneNo: payload.phoneNumber,
        EmailID: payload.emailId,
        Name: payload.fullName,
        CountryCode: payload.countryCode,
        PageUrl: window.location.href,
        CommConsent: formatDate(new Date()),
        UniqueId: getOrCreateUniqueLeadId(),
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to submit phone number");
    }
  } catch (error) {
    console.error("Error submitting phone number:", error);
  }
}

export interface CreateLeadOptions {
  form: Record<string, any>;
  meta: Record<string, any>;
  formExtra?: Record<string, any>;
  apiUrl?: string;
}

/**
 * POST /api/salesforce.com/lead-create-new only (program page — no lp-new).
 */
export async function createLead({ form, meta, formExtra, apiUrl }: CreateLeadOptions) {
  const response = await leadCreateFetch(apiUrl || "/api/salesforce.com/lead-create-new", {
    method: "POST",
    body: JSON.stringify({
      ipstack: getIpJSONData(),
      form: { ...form, ...formExtra },
      meta,
    }),
  });

  if (typeof response === "string") {
    return JSON.parse(response as unknown as string);
  }
  return response.json();
}

/** POST /api/prospect-activity — fire-and-forget. */
export function createProspectActivity(leadResponse: any) {
  fetch("/api/prospect-activity", {
    method: "post",
    body: JSON.stringify({ leadResponse }),
  }).then(() => {
    // do nothing — matches existing fire-and-forget behavior
  });
}

/** POST /api/connect-counsellor. */
export { connectCounsellor } from "@/utils/connectCounsellor";
